import {Component, OnInit, Inject, ViewChild} from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { BaseComponent } from '@app/shared/components/base/base.component';
import {AddressBookEntry, AddressBookEntryList} from "@app/shared/models/address-book-entry";
import {AddressBookEntryService, AddressBookEntryUriBuilderParams} from "@app/core/services/address-book-entry.service";
import {
    GridOptions,
    GridReadyEvent,
    GridSizeChangedEvent,
    RowClickedEvent,
    RowDoubleClickedEvent
} from "ag-grid-community";
import {AggWidgetHeaderComponent} from "@app/shared/components/agg-widget-header/agg-widget-header.component";
import {ResponseRow} from "@app/shared/models/response";
import {DrI18nService} from "@app/core/services/i18n/i18n.service";
import {AgGridAngular} from "ag-grid-angular";
import {TranslateService} from "@ngx-translate/core";


@Component({
    selector: 'app-address-book-search-dialog',
    templateUrl: './address-book-search-dialog.component.html',
    styleUrls: ['./address-book-search-dialog.component.scss']
})
export class AddressBookSearchDialogComponent extends BaseComponent implements OnInit {
    @ViewChild('agGridCaseWidget', { static: true }) agGrid: AgGridAngular;

    public list: AddressBookEntryList;
    public rowData: any[];
    public gridOptions: GridOptions;
    public searchText: string;
    public isSearched: boolean;


    constructor(
        public dialogRef: MatDialogRef<AddressBookSearchDialogComponent>,
        private addressBookEntryService: AddressBookEntryService,
        protected translateService: TranslateService,
        @Inject(MAT_DIALOG_DATA) public data
    ) {
        super();
        this._idPrefix = 'address_book_search_dialog.';
    }

    public ngOnInit() {
        this.gridOptions = this.getGridOptions(true);
    }

    private getGridOptions(hasRowClickHandler: boolean = true): GridOptions {
        return {
            debug                       : false,
            headerHeight                : 40,
            suppressLoadingOverlay: true,
            suppressMovableColumns      : true,
            suppressNoRowsOverlay       : true,
            suppressCellSelection       : true,
            rowSelection                : 'single',
            rowClass                    : 'dr-row-class mat-typography',
            domLayout                   : 'normal',
            frameworkComponents         : { agColumnHeader: AggWidgetHeaderComponent },
            onGridReady                 : (event: GridReadyEvent) => this.onGridReadyHandler(event),
            onGridSizeChanged           : (event: GridSizeChangedEvent) => this.onGridSizeChangedHandler(event),
            getRowNodeId                : (row: ResponseRow) => {
                let idx = row.id;
                return this.idPrefix + 'addressbook_search_result.row_' + idx;
            }
        }
    }

    private onGridReadyHandler(gridEvent: GridReadyEvent): void {
        this.agGrid.api.setColumnDefs([
            {
                field: 'name',
                headerName: this.translateService.instant("address_book_management.address_book_search_modal.table_header.name"),
                flex: 1,
                cellRenderer: this.cellRenderer
            },
            {
                field: 'email',
                headerName: this.translateService.instant("address_book_management.address_book_search_modal.table_header.email"),
                flex: 2,
                cellRenderer: this.cellRenderer
            },
            {
                field: 'phone',
                headerName: this.translateService.instant("address_book_management.address_book_search_modal.table_header.phone"),
                flex: 3,
                cellRenderer: this.cellRenderer
            },
            {
                field: 'address',
                headerName: this.translateService.instant("address_book_management.address_book_search_modal.table_header.address"),
                flex: 4,
                cellRenderer: this.cellRenderer
            }

        ]);
        gridEvent.api.sizeColumnsToFit();
    }

    private onGridSizeChangedHandler(gridEvent: GridSizeChangedEvent): void {
        gridEvent.api.sizeColumnsToFit();
    }

    private cellRenderer(params: any): any {

        if (params.column.colId === 'name' && ( params.data.name1 || params.data.name2 || params.data.name3) ) {
            let value = '';
            if(params.data.name1)
                value += params.data.name1;
            if(params.data.name2)
                value += ' ' + params.data.name2;
            if(params.data.name3)
                value += ' ' + params.data.name3;
            return value;
        }

        if (params.column.colId === 'email') {
            if(params.data.emails && params.data.emails.length && params.data.emails[0]) {
                return params.data.emails[0];
            } else {
                return '-';
            }
        }

        if (params.column.colId === 'phone') {
            return params.data.phone || '-';
        }

        if (params.column.colId === 'address') {
            return (params.data.address.postalCode + ' ' + params.data.address.city + ', ' + params.data.address.street);
        }

        // default:
        return params.value;
    }

    public doSearch(searchText: string): void {

        // clear search results if no search query data is provided:
        if(!searchText) {
            this.isSearched = false;
            this.agGrid.api.setRowData([]);
            return;
        }

        this.isSearched = true;

        const queryParams: any = {
            limit: 50,
            search: searchText //this.searchText
        };
        this.addressBookEntryService.query(this.generateUriBuilderParams(), { queryParams: queryParams })
            .subscribe((result: AddressBookEntryList) => {
                this.list = result;
                this.list.items.sort(AddressBookEntry.sortByActionListDisplayName);

                this.agGrid.api.setRowData(this.list.items);
            }, err => this.handleError(err));
    }

    protected generateUriBuilderParams(entry?: AddressBookEntry): AddressBookEntryUriBuilderParams {
        return new AddressBookEntryUriBuilderParams(entry);
    }

    public getSelection(): AddressBookEntry[] {
        return this.agGrid.api.getSelectedRows();
    }

    public hasSelection(): boolean {
        return this.agGrid.api && this.getSelection().length > 0;
    }

    public close(): void {
        this.dialogRef.close(null);
    }

    public ok(): void {
        this.dialogRef.close(this.getSelection()[0]);
    }
}
